import React from 'react';
import { 
  Modal, 
  Button, 
  Badge, 
  Row, 
  Col, 
  Image,
  Tab,
  Nav,
  Accordion,
  Card
} from 'react-bootstrap';
import { 
  IoClose, 
  IoCalendar, 
  IoEye, 
  IoHeart, 
  IoChatbubble,
  IoPerson,
  IoDocumentText,
  IoStar,
  IoShareSocial,
  IoPrint,
  IoDownload
} from 'react-icons/io5';
import { FaBook, FaTag, FaClock } from 'react-icons/fa';
import { formatDistanceToNow, format } from 'date-fns';
import { id } from 'date-fns/locale';

const ArtikelViewModal = ({ show, handleClose, artikel }) => {
  if (!artikel) return null;

  // Format tanggal
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMMM yyyy, HH:mm', { locale: id });
    } catch {
      return dateString;
    }
  };

  // Time ago
  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { 
        addSuffix: true,
        locale: id 
      });
    } catch {
      return '';
    }
  };

  // Status badge config
  const getStatusConfig = (status) => {
    const configs = {
      DRAFT: { variant: 'warning', label: 'Draft' },
      PUBLISHED: { variant: 'success', label: 'Published' },
      ARCHIVED: { variant: 'secondary', label: 'Archived' },
    };
    return configs[status] || { variant: 'light', label: status };
  };

  // Type badge config
  const getTypeConfig = (type) => {
    const configs = {
      TEKNOLOGI: { variant: 'info', label: 'Teknologi' },
      PENDIDIKAN: { variant: 'primary', label: 'Pendidikan' },
      KESEHATAN: { variant: 'danger', label: 'Kesehatan' },
      BISNIS: { variant: 'success', label: 'Bisnis' },
      OLAHRAGA: { variant: 'warning', label: 'Olahraga' },
    };
    return configs[type] || { variant: 'light', label: type };
  };

  const statusConfig = getStatusConfig(artikel.status);
  const typeConfig = getTypeConfig(artikel.type_artikel);

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      size="xl"
      centered
      backdrop="static"
    >
      <Modal.Header className="border-bottom-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <IoDocumentText size={24} className="text-primary" />
          <span>Detail Artikel</span>
        </Modal.Title>
        <Button 
          variant="link" 
          onClick={handleClose}
          className="p-0"
        >
          <IoClose size={28} />
        </Button>
      </Modal.Header>

      <Modal.Body className="pt-0">
        {/* Header dengan judul dan badges */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h3 className="mb-2">{artikel.judul}</h3>
            <div className="d-flex gap-2">
              <Badge bg={statusConfig.variant} className="px-3 py-2">
                {statusConfig.label}
              </Badge>
              <Badge bg={typeConfig.variant} className="px-3 py-2">
                {typeConfig.label}
              </Badge>
              {artikel.isFeatured && (
                <Badge bg="warning" className="px-3 py-2 d-flex align-items-center gap-1">
                  <IoStar size={14} /> Featured
                </Badge>
              )}
            </div>
          </div>
          
          <div className="d-flex flex-wrap gap-3 text-muted small mb-4">
            <span className="d-flex align-items-center gap-1">
              <IoCalendar /> Created: {formatDate(artikel.created_at)}
            </span>
            {artikel.updated_at && (
              <span className="d-flex align-items-center gap-1">
                <FaClock /> Updated: {formatDate(artikel.updated_at)}
              </span>
            )}
            {artikel.publishedAt && (
              <span className="d-flex align-items-center gap-1">
                <IoCalendar /> Published: {formatDate(artikel.publishedAt)}
              </span>
            )}
          </div>
        </div>

        {/* Thumbnail */}
        {artikel.thumbnail && (
          <div className="mb-4">
            <Image 
              src={artikel.thumbnail} 
              alt={artikel.judul}
              fluid
              rounded
              className="w-100"
              style={{ maxHeight: '400px', objectFit: 'cover' }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = 
                  '<div class="text-center py-5 border rounded bg-light"><small class="text-muted">Thumbnail not available</small></div>';
              }}
            />
          </div>
        )}

        {/* Stats Bar */}
        <div className="d-flex justify-content-between align-items-center p-3 mb-4 bg-light rounded">
          <div className="d-flex gap-4">
            <div className="text-center">
              <div className="d-flex align-items-center gap-1">
                <IoEye size={20} className="text-primary" />
                <span className="fw-bold fs-5">{artikel.views || 0}</span>
              </div>
              <small className="text-muted">Views</small>
            </div>
            <div className="text-center">
              <div className="d-flex align-items-center gap-1">
                <IoHeart size={20} className="text-danger" />
                <span className="fw-bold fs-5">{artikel.likes || 0}</span>
              </div>
              <small className="text-muted">Likes</small>
            </div>
            {artikel.allowComments && (
              <div className="text-center">
                <div className="d-flex align-items-center gap-1">
                  <IoChatbubble size={20} className="text-success" />
                  <span className="fw-bold fs-5">-</span>
                </div>
                <small className="text-muted">Comments</small>
              </div>
            )}
          </div>
          
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm">
              <IoShareSocial /> Share
            </Button>
            <Button variant="outline-secondary" size="sm">
              <IoPrint /> Print
            </Button>
            <Button variant="outline-success" size="sm">
              <IoDownload /> PDF
            </Button>
          </div>
        </div>

        {/* Tabs untuk detail */}
        <Tab.Container defaultActiveKey="content">
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="content">
                <IoDocumentText /> Konten
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="details">
                <FaBook /> Detail
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="metadata">
                <FaTag /> Metadata
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Tab Konten */}
            <Tab.Pane eventKey="content">
              <div className="p-3 border rounded">
                <h5 className="mb-3">Ringkasan</h5>
                <p className="mb-4">{artikel.ringkasan}</p>
                
                <h5 className="mb-3">Konten Lengkap</h5>
                <div 
                  className="mb-4"
                  style={{ 
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.8',
                    fontSize: '1.05rem'
                  }}
                >
                  {artikel.konten}
                </div>
              </div>
            </Tab.Pane>

            {/* Tab Detail */}
            <Tab.Pane eventKey="details">
              <Row className="g-3">
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title className="d-flex align-items-center gap-2 mb-3">
                        <IoPerson /> Informasi Penulis
                      </Card.Title>
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Created By:</span>
                          <span className="fw-semibold">
                            {artikel.created_by || 'System'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Updated By:</span>
                          <span className="fw-semibold">
                            {artikel.updated_by || 'System'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Published By:</span>
                          <span className="fw-semibold">
                            {artikel.published_by || '-'}
                          </span>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  <Card className="h-100">
                    <Card.Body>
                      <Card.Title className="d-flex align-items-center gap-2 mb-3">
                        <FaClock /> Timeline
                      </Card.Title>
                      <div className="d-flex flex-column gap-2">
                        <div className="d-flex justify-content-between">
                          <span className="text-muted">Created:</span>
                          <span>
                            {formatDate(artikel.created_at)}
                            <br />
                            <small className="text-muted">
                              ({getTimeAgo(artikel.created_at)})
                            </small>
                          </span>
                        </div>
                        {artikel.updated_at && (
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Last Updated:</span>
                            <span>
                              {formatDate(artikel.updated_at)}
                              <br />
                              <small className="text-muted">
                                ({getTimeAgo(artikel.updated_at)})
                              </small>
                            </span>
                          </div>
                        )}
                        {artikel.publishedAt && (
                          <div className="d-flex justify-content-between">
                            <span className="text-muted">Published:</span>
                            <span>
                              {formatDate(artikel.publishedAt)}
                              <br />
                              <small className="text-muted">
                                ({getTimeAgo(artikel.publishedAt)})
                              </small>
                            </span>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab.Pane>

            {/* Tab Metadata */}
            <Tab.Pane eventKey="metadata">
              <Accordion defaultActiveKey="0">
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Basic Information</Accordion.Header>
                  <Accordion.Body>
                    <Row className="g-2">
                      <Col xs={6} md={4}>
                        <div className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-muted">ID:</span>
                          <code className="small">{artikel.id}</code>
                        </div>
                      </Col>
                      <Col xs={6} md={4}>
                        <div className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-muted">Slug:</span>
                          <span className="fw-semibold">
                            {artikel.slug || '-'}
                          </span>
                        </div>
                      </Col>
                      <Col xs={6} md={4}>
                        <div className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-muted">Allow Comments:</span>
                          <Badge bg={artikel.allowComments ? 'success' : 'secondary'}>
                            {artikel.allowComments ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                      </Col>
                      <Col xs={6} md={4}>
                        <div className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-muted">Featured:</span>
                          <Badge bg={artikel.isFeatured ? 'warning' : 'secondary'}>
                            {artikel.isFeatured ? 'Yes' : 'No'}
                          </Badge>
                        </div>
                      </Col>
                      <Col xs={6} md={4}>
                        <div className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-muted">Status:</span>
                          <Badge bg={statusConfig.variant}>
                            {statusConfig.label}
                          </Badge>
                        </div>
                      </Col>
                      <Col xs={6} md={4}>
                        <div className="d-flex justify-content-between py-2 border-bottom">
                          <span className="text-muted">Type:</span>
                          <Badge bg={typeConfig.variant}>
                            {typeConfig.label}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="1">
                  <Accordion.Header>Statistics</Accordion.Header>
                  <Accordion.Body>
                    <Row className="g-2">
                      <Col xs={6} md={3}>
                        <Card className="text-center border-0 bg-light">
                          <Card.Body>
                            <div className="fs-3 fw-bold text-primary">
                              {artikel.views || 0}
                            </div>
                            <small className="text-muted">Total Views</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={6} md={3}>
                        <Card className="text-center border-0 bg-light">
                          <Card.Body>
                            <div className="fs-3 fw-bold text-danger">
                              {artikel.likes || 0}
                            </div>
                            <small className="text-muted">Total Likes</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={6} md={3}>
                        <Card className="text-center border-0 bg-light">
                          <Card.Body>
                            <div className="fs-3 fw-bold text-success">
                              {artikel.comments || 0}
                            </div>
                            <small className="text-muted">Comments</small>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col xs={6} md={3}>
                        <Card className="text-center border-0 bg-light">
                          <Card.Body>
                            <div className="fs-3 fw-bold text-warning">
                              {artikel.shares || 0}
                            </div>
                            <small className="text-muted">Shares</small>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="2">
                  <Accordion.Header>Technical Details</Accordion.Header>
                  <Accordion.Body>
                    <div className="mb-3">
                      <small className="text-muted d-block mb-1">Content Length:</small>
                      <div className="d-flex gap-3">
                        <Badge bg="info">
                          {artikel.ringkasan?.length || 0} chars (Ringkasan)
                        </Badge>
                        <Badge bg="info">
                          {artikel.konten?.length || 0} chars (Konten)
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <small className="text-muted d-block mb-1">Last Activity:</small>
                      <div>
                        {artikel.last_activity || 'No recent activity'}
                      </div>
                    </div>

                    <div>
                      <small className="text-muted d-block mb-1">Storage Info:</small>
                      <div className="d-flex gap-3">
                        <Badge bg="secondary">
                          Thumbnail: {artikel.thumbnail ? 'Available' : 'Not set'}
                        </Badge>
                      </div>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>

      <Modal.Footer className="border-top-0">
        <Button variant="secondary" onClick={handleClose}>
          <IoClose /> Close
        </Button>
        <Button variant="outline-primary">
          <IoShareSocial /> Share Artikel
        </Button>
        <Button variant="primary">
          Edit Artikel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ArtikelViewModal;